import i18nFactory from '@yandex-int/i18n';
import { Epic, ofType } from 'redux-observable';
import { concat, EMPTY, from, Observable, of } from 'rxjs';
import { catchError, filter, map, mergeMap, takeUntil } from 'rxjs/operators';

import { Action } from '../../actions';
import { showCommonDataFetchError } from '../../actions/common';
import {
    closeMinusPhraseLibraryModal,
    CloseMinusPhraseLibraryModal,
    CREATE_MINUS_PHRASE_LIBRARY_LIST_DATA,
    DELETE_MINUS_PHRASE_LIBRARY_LIST,
    DeleteMinusPhraseLibraryList,
    EDIT_MINUS_PHRASE_LIBRARY_LIST_DATA,
    HANDLE_MINUS_PHRASE_LIBRARY_COLUMNS_WIDTH_UPDATE,
    HandleMinusPhraseLibraryColumnsWidthUpdate,
    OPEN_MINUS_PHRASE_LIBRARY_ADD_LIST_MODAL,
    OPEN_MINUS_PHRASE_LIBRARY_EDIT_LIST_MODAL,
    OpenMinusPhraseLibraryAddListModal,
    OpenMinusPhraseLibraryEditListModal,
    REQUEST_MINUS_PHRASE_LIBRARY_GRID_ROW,
    requestMinusPhraseLibraryGridRow,
    RequestMinusPhraseLibraryGridRow,
    RESET_MINUS_PHRASE_LIBRARY_GRID_DATA,
    resetMinusPhraseLibraryGridData,
    ResetMinusPhraseLibraryGridData,
    showMinusPhraseLibraryErrorListData,
    ShowMinusPhraseLibraryErrorListData,
    updateMinusPhraseLibraryGridColumnsWidth,
    updateMinusPhraseLibraryGridData,
    updateMinusPhraseLibraryModalStatus,
    updateMinusPhraseListData
} from '../../actions/minusPhraseLibrary';
import { FetchMinusPhraseLibraryGridDataResult } from '../../api/MinusPhraseLibraryProvider/MinusPhraseLibraryProvider';
import { MinusPhraseMutationResultData } from '../../api/MinusPhraseLibraryProvider/MinusPhraseMutationExecutor';
import { formatMinusPhrasesToApi, validateMinusPhraseData } from '../../mappers/minus-phrase/minus-phrase';
import { selectMinusPhrasesLibraryConstants } from '../../selectors/minusPhraseLibrary/minusPhraseLibrary';
import { getColumnsWidthSettings } from '../../service/ActionHelpers';
import { confirm } from '../../service/Confirm/Confirm';
import { ApiMethodExecutingError } from '../../service/Errors';
import { showNoticeMessage } from '../../service/GridHelpers/GridHelpers';
import '../../service/Rx';
import { RequestIndexResult } from '../../service/SegmentManager';
import { LocalStorage, StorageKeyType } from '../../service/Storage/Storage';
import { EpicDeps } from '../../service/StoreFactory';
import { minusPhraseLibrary } from '../../typings/common';
import { DnaEpicAction } from '../../typings/epic';
import { NoticeType } from '../../typings/grid';
import { MinusPhraseLibraryModalType, MinusPhraseLibraryRow } from '../../typings/minusPhraseLibrary';
import { State } from '../../typings/state';

import * as keyset from './minusPhraseLibrary.i18n';

const i18n = i18nFactory(keyset);

const requestMinusPhraseLibraryGridRowEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(REQUEST_MINUS_PHRASE_LIBRARY_GRID_ROW),
        map(({ index }: RequestMinusPhraseLibraryGridRow) => {
            return deps.segmentManager.requestIndex(index);
        }),
        filter(res => res !== null),
        filter(({ isNeedFetch }: RequestIndexResult) => isNeedFetch),
        mergeMap(
            ({ offset, count }: RequestIndexResult): Observable<Action> => {
                const { login } = store$.value.commonData;

                return from(
                    deps.minusPhraseLibraryDataProvider.fetchMinusPhraseLibraryGridData(offset, count, login)
                ).pipe(
                    takeUntil(action$.ofType(RESET_MINUS_PHRASE_LIBRARY_GRID_DATA)),
                    map((response: FetchMinusPhraseLibraryGridDataResult) => {
                        deps.segmentManager.setTotal(response.totalCount);

                        const state = store$.value;

                        return updateMinusPhraseLibraryGridData({
                            data: response.data.reduce(
                                (acc: MinusPhraseLibraryRow[], item: MinusPhraseLibraryRow, pos: number) => {
                                    acc[offset + pos] = item;

                                    return acc;
                                },
                                state.minusPhraseLibrary.gridData.data.slice()
                            ),
                            total: response.totalCount,
                            allItemsIds: [],
                            campaignsIds: response.campaignsIds
                        });
                    })
                );
            }
        ),
        catchError((err: ApiMethodExecutingError) => {
            return of(showCommonDataFetchError(err));
        })
    );
};

const handleMinusPhraseLibraryColumnsWidthUpdateEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$) => {
    return action$
        .ofType(HANDLE_MINUS_PHRASE_LIBRARY_COLUMNS_WIDTH_UPDATE)
        .map((action: HandleMinusPhraseLibraryColumnsWidthUpdate) => {
            const { columnsSettings } = store$.value.minusPhraseLibrary;

            const settings = getColumnsWidthSettings(
                columnsSettings,
                action.key,
                action.width,
                minusPhraseLibrary,
                false
            );

            LocalStorage.setItem(StorageKeyType.minusPhraseLibrarySettings, settings);

            return updateMinusPhraseLibraryGridColumnsWidth(settings);
        });
};

const resetMinusPhraseLibraryEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.ofType(RESET_MINUS_PHRASE_LIBRARY_GRID_DATA).map(() => {
        deps.segmentManager.reset();

        return requestMinusPhraseLibraryGridRow(0);
    });
};

const openMinusPhraseLibraryAddListModalEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$) => {
    return action$.ofType(OPEN_MINUS_PHRASE_LIBRARY_ADD_LIST_MODAL).mergeMap(
        ({ index }: OpenMinusPhraseLibraryAddListModal): DnaEpicAction => {
            let updateAction$: DnaEpicAction = EMPTY;

            if (index) {
                const data = store$.value.minusPhraseLibrary.gridData.data[index];

                updateAction$ = of(updateMinusPhraseListData(`${i18n('copy')} ${data.name}`, data.minusKeywords));
            }

            return concat(
                updateAction$,
                of(updateMinusPhraseLibraryModalStatus(MinusPhraseLibraryModalType.create, true))
            );
        }
    );
};

const openMinusPhraseLibraryEditListModalEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$
        .ofType(OPEN_MINUS_PHRASE_LIBRARY_EDIT_LIST_MODAL)
        .mergeMap(({ index }: OpenMinusPhraseLibraryEditListModal) => {
            const data = store$.value.minusPhraseLibrary.gridData.data[index];

            return [
                updateMinusPhraseListData(data.name, data.minusKeywords),
                updateMinusPhraseLibraryModalStatus(MinusPhraseLibraryModalType.edit, true)
            ];
        });
};

const createMinusPhraseListDataEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.ofType(CREATE_MINUS_PHRASE_LIBRARY_LIST_DATA).mergeMap(():
        | Observable<
              ShowMinusPhraseLibraryErrorListData | ResetMinusPhraseLibraryGridData | CloseMinusPhraseLibraryModal
          >
        | Array<ShowMinusPhraseLibraryErrorListData> => {
        const state = store$.value;
        const constants = selectMinusPhrasesLibraryConstants(state);
        if (!constants) {
            return EMPTY;
        }
        const { title = '', minusPhrases = '' } = state.minusPhraseLibrary.minusPhraseList;
        const clientValidationResult = validateMinusPhraseData(
            state.minusPhraseLibrary.minusPhraseList,
            'addItems',
            constants
        );

        if (clientValidationResult.length) {
            return [showMinusPhraseLibraryErrorListData(clientValidationResult)];
        }

        return Observable.fromPromise(
            deps.minusPhraseLibraryMutationExecutor.addMinusKeywordsPack({
                addItems: [
                    {
                        name: title,
                        minusKeywords: formatMinusPhrasesToApi(minusPhrases)
                    }
                ]
            })
        )
            .mergeMap(
                (
                    response: MinusPhraseMutationResultData
                ): Array<
                    ShowMinusPhraseLibraryErrorListData | ResetMinusPhraseLibraryGridData | CloseMinusPhraseLibraryModal
                > => {
                    const { validationResult } = response;
                    if (validationResult) {
                        return [showMinusPhraseLibraryErrorListData(validationResult)];
                    }

                    showNoticeMessage(
                        NoticeType.success,
                        i18n('create-success', {
                            name: title
                        })
                    );

                    return [resetMinusPhraseLibraryGridData(), closeMinusPhraseLibraryModal()];
                }
            )
            .catch((err: ApiMethodExecutingError) => {
                return Observable.of(showMinusPhraseLibraryErrorListData(err.getErrors()));
            });
    });
};

const updateMinusPhraseListDataEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.ofType(EDIT_MINUS_PHRASE_LIBRARY_LIST_DATA).mergeMap(():
        | Observable<
              ShowMinusPhraseLibraryErrorListData | ResetMinusPhraseLibraryGridData | CloseMinusPhraseLibraryModal
          >
        | Array<ShowMinusPhraseLibraryErrorListData> => {
        const state = store$.value;
        const constants = selectMinusPhrasesLibraryConstants(state);
        if (!constants) {
            return EMPTY;
        }
        const { title = '', minusPhrases = '', id = '' } = state.minusPhraseLibrary.minusPhraseList;
        const clientValidationResult = validateMinusPhraseData(
            state.minusPhraseLibrary.minusPhraseList,
            'updateItems',
            constants
        );

        if (clientValidationResult.length) {
            return [showMinusPhraseLibraryErrorListData(clientValidationResult)];
        }

        return Observable.fromPromise(
            deps.minusPhraseLibraryMutationExecutor.updateMinusKeywordsPacks({
                updateItems: [
                    {
                        id: id,
                        name: title,
                        minusKeywords: formatMinusPhrasesToApi(minusPhrases)
                    }
                ]
            })
        )
            .mergeMap(
                (
                    response: MinusPhraseMutationResultData
                ): Array<
                    ShowMinusPhraseLibraryErrorListData | ResetMinusPhraseLibraryGridData | CloseMinusPhraseLibraryModal
                > => {
                    const { validationResult } = response;

                    if (validationResult) {
                        return [showMinusPhraseLibraryErrorListData(validationResult)];
                    }

                    showNoticeMessage(
                        NoticeType.success,
                        i18n('update-success', {
                            name: title
                        })
                    );

                    return [resetMinusPhraseLibraryGridData(), closeMinusPhraseLibraryModal()];
                }
            )
            .catch((err: ApiMethodExecutingError) => {
                return Observable.of(showMinusPhraseLibraryErrorListData(err.getErrors()));
            });
    });
};

const deleteMinusPhraseListEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.ofType(DELETE_MINUS_PHRASE_LIBRARY_LIST).mergeMap(
        (
            action: DeleteMinusPhraseLibraryList
        ): Observable<
            ShowMinusPhraseLibraryErrorListData | ResetMinusPhraseLibraryGridData | CloseMinusPhraseLibraryModal
        > => {
            const { id, name } = store$.value.minusPhraseLibrary.gridData.data[action.index];

            return Observable.fromPromise(
                confirm({
                    message: i18n('confirm-delete')
                })
            )
                .mergeMap(() =>
                    Observable.fromPromise(
                        deps.minusPhraseLibraryMutationExecutor.deleteMinusKeywordsPacks({
                            minusKeywordPackIds: [id]
                        })
                    )
                        .do((response: MinusPhraseMutationResultData) => {
                            if (response.validationResult) {
                                showNoticeMessage(
                                    NoticeType.error,
                                    i18n('delete-error', {
                                        name
                                    })
                                );
                            } else {
                                showNoticeMessage(
                                    NoticeType.success,
                                    i18n('delete-success', {
                                        name
                                    })
                                );
                            }
                        })
                        .mapTo(resetMinusPhraseLibraryGridData())
                        .catch(() => {
                            showNoticeMessage(
                                NoticeType.error,
                                i18n('delete-error', {
                                    name: name
                                })
                            );

                            return Observable.of();
                        })
                )
                .catch(() => {
                    return Observable.of();
                });
        }
    );
};

export {
    requestMinusPhraseLibraryGridRowEpic,
    handleMinusPhraseLibraryColumnsWidthUpdateEpic,
    resetMinusPhraseLibraryEpic,
    openMinusPhraseLibraryAddListModalEpic,
    openMinusPhraseLibraryEditListModalEpic,
    deleteMinusPhraseListEpic,
    createMinusPhraseListDataEpic,
    updateMinusPhraseListDataEpic
};
